// In dev: API_URL is '' so requests use the webpack-dev-server proxy (/api/* → localhost:8080).
// In production: API_URL is the real backend origin injected by DefinePlugin from .env.production.
export const API_URL: string = process.env.API_URL;

const TOKEN_KEY = 'lifebit.auth';

/**
 * The dashboard never holds its tokens. It logs in with cookie transport: the
 * backend returns the access and refresh tokens as HttpOnly cookies that page
 * scripts can't read, and the JSON body carries only the profile fields below.
 * State-changing requests echo the readable lb_csrf cookie in X-CSRF-Token.
 */
const CLIENT_HEADERS: Record<string, string> = {
  'X-LB-Client': 'dashboard',
  'X-LB-Auth-Transport': 'cookie',
};
const CSRF_COOKIE = 'lb_csrf';

/** What is kept in localStorage: display and permission data only, never a token. */
export interface AuthSession {
  token_type: string;
  expires_at: string;
  role: string;
  user_name: string;
  username?: string;
  is_super_admin?: boolean;
  registration_complete?: boolean;
  permissions?: string[];
  session_id?: number;
}

export interface LoginResponse extends AuthSession {}

/** Login returned a 2FA enrollment challenge (Flow A) — user is not yet enrolled. */
export interface TwoFactorSetupChallenge {
  requires_2fa_setup: true;
  setup_token: string;
}

/** Login returned a 2FA code challenge (Flow B) — user is already enrolled. */
export interface TwoFactorLoginChallenge {
  requires_2fa: true;
  pending_token: string;
}

/**
 * Result of a dashboard login. On a correctly-configured dashboard this is
 * always one of the two 2FA challenges; a full `AuthSession` only comes back
 * for non-dashboard sources and is not expected here.
 */
export type LoginResult = AuthSession | TwoFactorSetupChallenge | TwoFactorLoginChallenge;

export function isSetupChallenge(r: LoginResult): r is TwoFactorSetupChallenge {
  return (r as TwoFactorSetupChallenge).requires_2fa_setup === true;
}

export function isLoginChallenge(r: LoginResult): r is TwoFactorLoginChallenge {
  return (r as TwoFactorLoginChallenge).requires_2fa === true;
}

/** Thrown on HTTP 429; `retryAfter` is the lockout window in seconds. */
export class RateLimitError extends Error {
  retryAfter: number;
  constructor(message: string, retryAfter: number) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

/** Thrown when a short-lived setup/pending token has expired and login must restart. */
export class TokenExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenExpiredError';
  }
}

export function getSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AuthSession & { token?: string };
    // A session saved by a build from before cookie transport still carries a
    // bearer token the backend no longer accepts from the dashboard. Treat it
    // as signed out, and don't leave the token sitting in storage.
    if (session.token) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function setSession(session: AuthSession): void {
  // Belt and braces: the backend omits tokens in cookie mode, but never
  // persist one even if a response did include it.
  const { token: _token, refresh_token: _refresh, ...safe } = session as AuthSession & {
    token?: string;
    refresh_token?: string;
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(safe));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Identifies the signed-in session for caches that must not leak across
 * logins. Not a credential.
 */
export function sessionKey(): string | null {
  const s = getSession();
  if (!s) return null;
  return s.session_id != null ? `session:${s.session_id}` : `user:${s.username ?? s.user_name}`;
}

function readCookie(name: string): string {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
}

/** Headers every dashboard request carries: client identity plus CSRF token. */
export function authHeader(): Record<string, string> {
  const headers: Record<string, string> = { ...CLIENT_HEADERS };
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return headers;
}

/** Ends the session on the server (revoking it and clearing its cookies). */
export async function logout(): Promise<void> {
  try {
    await apiFetch('/api/v1/auth/logout', { method: 'POST' });
  } catch {
    /* signing out locally still matters if the call fails */
  } finally {
    clearSession();
  }
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let message = `${fallback} (${res.status})`;
    try {
      const errorBody = await res.json();
      if (errorBody?.message) message = errorBody.message;
      else if (errorBody?.error) message = errorBody.error;
    } catch {
      /* ignore */
    }
    if (res.status === 429) {
      const parsed = parseInt(res.headers.get('Retry-After') || '', 10);
      throw new RateLimitError(message, Number.isFinite(parsed) && parsed > 0 ? parsed : 300);
    }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

/**
 * Phase 1 of dashboard login. Does NOT persist a session: a correctly
 * configured dashboard always answers with a 2FA challenge (Flow A or B),
 * which the caller must complete before a session exists.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const data = await postJson<LoginResult>(
    '/api/v1/auth/login',
    { email, password, source: 'dashboard' },
    'Login failed'
  );
  // Only non-dashboard sources return a usable session in one step; its
  // tokens are already in HttpOnly cookies by the time this resolves.
  if (!isSetupChallenge(data) && !isLoginChallenge(data)) {
    setSession(data);
  }
  return data;
}

export interface TwoFactorSetupInfo {
  qr_code: string;
  secret: string;
  issuer: string;
  account_name: string;
}

/** Flow A — generate the pending TOTP secret and QR code for enrollment. */
export async function setupTwoFactor(setupToken: string): Promise<TwoFactorSetupInfo> {
  try {
    return await postJson<TwoFactorSetupInfo>(
      '/api/v1/2fa/setup',
      { setup_token: setupToken },
      'Failed to start two-factor setup'
    );
  } catch (err) {
    if (err instanceof Error && /setup_token|session is required/i.test(err.message)) {
      throw new TokenExpiredError(err.message);
    }
    throw err;
  }
}

export interface TwoFactorEnrollmentResult extends AuthSession {
  backup_codes: string[];
  totp_enabled?: boolean;
  message?: string;
}

/** Flow A — confirm the first code, enable 2FA, and start the session. */
export async function verifyTwoFactorSetup(
  setupToken: string,
  code: string
): Promise<TwoFactorEnrollmentResult> {
  let data: TwoFactorEnrollmentResult;
  try {
    data = await postJson<TwoFactorEnrollmentResult>(
      '/api/v1/2fa/verify-setup',
      { setup_token: setupToken, code },
      'Failed to verify code'
    );
  } catch (err) {
    // A 401 here is ambiguous: either a wrong code (retryable) or an expired
    // setup_token (must restart). Distinguish by message.
    if (err instanceof Error && /setup_token|session is required/i.test(err.message)) {
      throw new TokenExpiredError(err.message);
    }
    throw err;
  }
  // Persist the session but never the single-use backup codes.
  const { backup_codes, ...session } = data;
  setSession(session as AuthSession);
  return data;
}

/** Flow B — complete an enrolled user's login with a TOTP or backup code. */
export async function verifyTwoFactorLogin(
  pendingToken: string,
  code: string
): Promise<AuthSession> {
  let data: AuthSession;
  try {
    data = await postJson<AuthSession>(
      '/api/v1/2fa/verify-login',
      { pending_token: pendingToken, code },
      'Failed to verify code'
    );
  } catch (err) {
    if (err instanceof Error && /pending token/i.test(err.message)) {
      throw new TokenExpiredError(err.message);
    }
    throw err;
  }
  setSession(data);
  return data;
}

export interface ForgotPasswordResponse {
  message: string;
}

export async function requestPasswordReset(email: string): Promise<ForgotPasswordResponse> {
  return postJson('/api/v1/auth/forgot-password', { email }, 'Failed to request reset code');
}

export interface VerifyOtpResponse {
  expires_at: string;
  reset_key: string;
}

export async function verifyOtp(email: string, otp: string): Promise<VerifyOtpResponse> {
  return postJson('/api/v1/auth/verify-otp', { email, otp }, 'Failed to verify code');
}

export interface ResetPasswordResponse {
  message: string;
}

export async function resetPassword(
  resetKey: string,
  newPassword: string
): Promise<ResetPasswordResponse> {
  return postJson(
    '/api/v1/auth/reset-password',
    { reset_key: resetKey, new_password: newPassword },
    'Failed to reset password'
  );
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

// One refresh at a time: the backend rotates the refresh token on every use and
// treats a replayed one as theft, so parallel 401s must share a single call.
let refreshing: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', ...authHeader() },
  })
    .then(async (res) => {
      if (!res.ok) return false;
      // Keep the cached permissions in step with the server.
      const data = (await res.json()) as Partial<AuthSession>;
      const current = getSession();
      if (current) {
        setSession({
          ...current,
          role: data.role ?? current.role,
          is_super_admin: data.is_super_admin ?? current.is_super_admin,
          permissions: data.permissions ?? current.permissions,
          expires_at: data.expires_at ?? current.expires_at,
        });
      }
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function isTokenExpired(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { code?: string };
    return body.code === 'token_expired';
  } catch {
    return false;
  }
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers || {});
    headers.delete('Authorization');
    Object.entries(authHeader()).forEach(([k, v]) => headers.set(k, v));
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    return fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
  };

  let res = await send();
  if (res.status === 401 && (await isTokenExpired(res)) && (await refreshSession())) {
    res = await send();
  }
  if (res.status === 401) {
    unauthorizedListeners.forEach((listener) => listener());
  }
  return res;
}
